'use client'

import { useCallback, useEffect, useState } from 'react'

import useCreateShareItinerary from '@/api/useCreateShareItinerary'
import { TextInputStandalone } from '../../Form/TextInput/TextInput'
import Modal, { useModal } from '../../Modal/Modal'

export const SHARE_MODAL_ID = 'share-itinerary-modal'

interface ShareItineraryModalProps {
  orderId?: string
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export default function ShareItineraryModal({
  orderId: orderIdProp,
  onSuccess,
  onError,
}: ShareItineraryModalProps) {
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [orderId, setOrderId] = useState<string>(orderIdProp || '')

  const shareItineraryMutation = useCreateShareItinerary()
  const hideDialog = useModal(
    (state: { hideDialog: (id: string) => void }) => state.hideDialog,
  )
  const getDialogArgs = useModal((state) => state.getDialogArgs)

  // Get orderId from dialog args when modal is opened (for order-tracking page)
  useEffect(() => {
    const args = getDialogArgs(SHARE_MODAL_ID)
    if (args?.orderId) {
      setOrderId(args.orderId)
    } else if (orderIdProp) {
      setOrderId(orderIdProp)
    }
  }, [getDialogArgs, orderIdProp, setOrderId])

  const validateEmail = (email: string): boolean => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!email) {
      setEmailError('Email is required')
      return false
    }
    if (!emailPattern.test(email)) {
      setEmailError('Please provide a valid email address')
      return false
    }
    setEmailError(null)
    return true
  }

  const onSubmit = useCallback(async () => {
    if (!validateEmail(email)) {
      return
    }

    try {
      setErrorMessage(null)
      const result = await shareItineraryMutation.mutateAsync({
        orderId,
        email,
      })

      if (result.success) {
        setIsSuccess(true)
        onSuccess?.()
        // Reset form and close modal after a short delay
        setTimeout(() => {
          setEmail('')
          setEmailError(null)
          setIsSuccess(false)
          hideDialog(SHARE_MODAL_ID)
        }, 2000)
      } else {
        setErrorMessage(result.message || 'Failed to share itinerary')
        onError?.(new Error(result.message || 'Failed to share itinerary'))
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'An unexpected error occurred'
      setErrorMessage(errorMsg)
      onError?.(error instanceof Error ? error : new Error(errorMsg))
    }
  }, [orderId, shareItineraryMutation, onSuccess, onError, email, hideDialog])

  const handleEmailChange = useCallback(
    (value?: string | number) => {
      if (typeof value === 'string') {
        setEmail(value)
        if (emailError) {
          validateEmail(value)
        }
      }
    },
    [emailError],
  )

  const isSubmitting = shareItineraryMutation.isPending

  return (
    <Modal
      id={SHARE_MODAL_ID}
      title={<span className="type-body-1 text-center">Share Itinerary</span>}
      dialogPanelClass="w-full max-w-[90vw] md:!max-w-[70rem] md:!w-[30vw]"
      content={
        <div className="mt-2">
          {isSuccess ? (
            <div className="text-center">
              <div className="text-green-600 mb-4">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Itinerary Shared Successfully!
              </h3>
              <p className="text-gray-600">
                The itinerary has been sent to the provided email address.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <TextInputStandalone
                label="Email Address*"
                name="email"
                placeholder="Enter email address"
                value={email}
                onChange={handleEmailChange}
                error={emailError}
                type="email"
                autoFocus
              />

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-red-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      }
      buttons={
        isSuccess
          ? undefined
          : [
              {
                text: isSubmitting ? 'Sharing...' : 'Share Itinerary',
                onCTAClick: onSubmit,
              },
            ]
      }
      persistent={false}
    />
  )
}
